import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Modal, Advice, Spacer, Markdown } from '@Sage/hoi-poi-ui';

import { PRODUCTS } from 'constants/Entities';
import { canCustomizeValueListFromEntityCrud } from 'utils/crud';
import { getBackendBoolean } from '@Sage/ssm-utils/backendScalars';
import { getLiteral } from 'utils/getLiteral';
import { isSageImplementation } from 'utils/sage';
import { errorToast } from 'utils/toast';
import { getRoute } from 'utils/routes';
import { logEvent } from 'utils/tracking';
import {
    getDecimalsByConfig,
    DECIMAL_TYPES,
    normalizeDecimalValue,
    roundDecimalByConfig,
} from 'utils/decimal';
import {
    addProductUoMRelation,
    checkProductUoMRelationInUse,
    updateProductUoMRelation,
} from 'services/ProductsService';

import Select from 'containers/components/Fields/Select';
import Text from 'containers/components/Fields/Text';
import Decimal from 'containers/components/Fields/Decimal';
import Bool from 'containers/components/Fields/Bool';

import './styles.scss';

/** Builds select label "Name (symbol)" from a value-list row or saved relation payload. */
const formatUnitOfMeasureOptionLabel = (row) => {
    if (!row) return '';
    const base = row.descripcion || row.description || row.label || row.descripcionValue || '';
    const sym = String(row.simbolo || row.symbol || row.strSimbolo || '').trim();
    if (!sym) return base;
    return `${base} (${sym})`;
};

const EMPTY_FORM = {
    name: '',
    unitOfMeasure: null,
    isBase: false,
    factor: '',
    minStep: '',
    useSpecialPrice: false,
    specialPrice: '',
    isActive: true,
    intOrder: '1',
};

const ProductUnitRelationsModal = memo(({ idCurrency, idEnvironment, onRef, onSaveSuccess }) => {
    const [open, setOpen] = useState(false);
    const [errors, setErrors] = useState({});
    const [form, setForm] = useState(EMPTY_FORM);
    const [loading, setLoading] = useState(false);
    /** Edit only: `null` while checking B2; `true` = SO lines use this relation (lock factor/minStep); `false` = editable. */
    const [relationInUse, setRelationInUse] = useState(null);
    /** `isBase` as loaded from API for this open; UI locks base/active rules only for persisted base, not draft toggles. */
    const [persistedIsBase, setPersistedIsBase] = useState(false);

    const canManageUomStandardValueList = useSelector((state) =>
        canCustomizeValueListFromEntityCrud(state?.config?.permission, PRODUCTS),
    );
    const isSageProductMode = useSelector((state) =>
        isSageImplementation(state?.config?.productModeCode),
    );
    const productCrud = useSelector(
        (state) => state.config.permission.crud_permission[PRODUCTS.permission],
    );

    const isEdit = !!form.id;
    const canSave = isEdit ? productCrud?.update : productCrud?.create;
    const lockFactorAndMinStep = isEdit && relationInUse !== false;

    const specialPriceDecimals = useMemo(
        () =>
            getDecimalsByConfig({
                type: DECIMAL_TYPES.CURRENCY_PRICES,
                idEnvironment,
                idCurrency: idCurrency !== '-1' ? idCurrency : undefined,
                entityName: 'PRODUCTS',
                fieldId: 'precio',
            }),
        [idCurrency, idEnvironment],
    );

    useEffect(() => {
        if (!onRef) return;
        onRef({
            open(payload = {}) {
                const {
                    idProduct,
                    id,
                    unitOfMeasure,
                    isBase,
                    isActive,
                    useSpecialPrice,
                    intOrder,
                    ...rest
                } = payload;
                const formattedSpecialPrice =
                    rest.specialPrice !== null &&
                    rest.specialPrice !== undefined &&
                    rest.specialPrice !== ''
                        ? roundDecimalByConfig({
                              value: rest.specialPrice,
                              type: DECIMAL_TYPES.CURRENCY_PRICES,
                              idEnvironment,
                              idCurrency: idCurrency !== '-1' ? idCurrency : undefined,
                              entityName: 'PRODUCTS',
                              fieldId: 'precio',
                          })
                        : rest.specialPrice;
                const resolvedIsBase = getBackendBoolean(isBase);
                setPersistedIsBase(Boolean(id) && resolvedIsBase);
                setForm({
                    ...EMPTY_FORM,
                    ...rest,
                    id,
                    idProduct,
                    specialPrice: formattedSpecialPrice,
                    intOrder:
                        intOrder != null && intOrder !== ''
                            ? String(intOrder)
                            : EMPTY_FORM.intOrder,
                    isBase: resolvedIsBase,
                    isActive: resolvedIsBase
                        ? true
                        : getBackendBoolean(isActive ?? EMPTY_FORM.isActive),
                    useSpecialPrice: getBackendBoolean(useSpecialPrice),
                    unitOfMeasure: unitOfMeasure
                        ? {
                              ...unitOfMeasure,
                              value: unitOfMeasure.id,
                              label: formatUnitOfMeasureOptionLabel({
                                  ...unitOfMeasure,
                                  descripcion:
                                      unitOfMeasure.descripcion || unitOfMeasure.description,
                              }),
                          }
                        : null,
                });
                setErrors({});
                if (id) {
                    setRelationInUse(null);
                    checkProductUoMRelationInUse({ idRelation: id })
                        .then((inUse) => setRelationInUse(!!inUse))
                        .catch((e) => {
                            console.error(e);
                            setRelationInUse(true);
                        });
                } else {
                    setRelationInUse(false);
                }
                setOpen(true);
            },
        });
    }, [idCurrency, idEnvironment, onRef]);

    const onCancel = useCallback(() => {
        setOpen(false);
        setLoading(false);
        setRelationInUse(null);
        setPersistedIsBase(false);
    }, []);

    const onChangeField = useCallback(
        (field) => (value) => setForm((current) => ({ ...current, [field]: value })),
        [],
    );

    const onChangeIsBase = useCallback((value) => {
        setForm((current) => ({
            ...current,
            isBase: value,
            ...(value ? { isActive: true } : {}),
        }));
    }, []);

    const validate = useCallback(() => {
        const newErrors = {};
        if (!form.name) newErrors.name = getLiteral('helptext_required_field');
        if (!form.unitOfMeasure) newErrors.unitOfMeasure = getLiteral('helptext_required_field');

        // Use normalizeDecimalValue (not parseFloat): API/state may use "0,01" and parseFloat("0,01") is 0.
        if (!lockFactorAndMinStep) {
            const factorNum = normalizeDecimalValue(form.factor);
            if (form.factor === '' || form.factor === null || form.factor === undefined) {
                newErrors.factor = getLiteral('helptext_required_field');
            } else if (factorNum === null || factorNum <= 0) {
                newErrors.factor = getLiteral('helptext_value_must_be_positive');
            }

            const minStepNum = normalizeDecimalValue(form.minStep);
            if (form.minStep === '' || form.minStep === null || form.minStep === undefined) {
                newErrors.minStep = getLiteral('helptext_required_field');
            } else if (minStepNum === null || minStepNum <= 0) {
                newErrors.minStep = getLiteral('helptext_value_must_be_positive');
            }
        }

        if (form.useSpecialPrice) {
            const priceNum = normalizeDecimalValue(form.specialPrice);
            if (
                form.specialPrice === '' ||
                form.specialPrice === null ||
                form.specialPrice === undefined
            ) {
                newErrors.specialPrice = getLiteral('helptext_required_field');
            } else if (priceNum === null) {
                newErrors.specialPrice = getLiteral('helptext_required_field');
            }
        }

        return newErrors;
    }, [form, lockFactorAndMinStep]);

    const onConfirm = useCallback(() => {
        if (!canSave) return;
        const newErrors = validate();
        if (Object.keys(newErrors).length) {
            setErrors(newErrors);
            return;
        }

        const idUnitOfMeasure = form.unitOfMeasure?.value ?? form.unitOfMeasure?.id;

        const payload = {
            idProduct: form.idProduct,
            idUnitOfMeasure,
            name: form.name,
            isBase: !!form.isBase,
            isActive: form.isBase ? true : !!form.isActive,
            useSpecialPrice: !!form.useSpecialPrice,
            specialPrice: form.useSpecialPrice ? normalizeDecimalValue(form.specialPrice) : null,
            intOrder: form.intOrder,
        };
        if (!lockFactorAndMinStep) {
            payload.factor = normalizeDecimalValue(form.factor);
            payload.minStep = normalizeDecimalValue(form.minStep);
        }
        if (form.extId != null && String(form.extId).trim() !== '') {
            payload.extId = form.extId;
        }

        const call = isEdit ? updateProductUoMRelation : addProductUoMRelation;
        if (isEdit) {
            payload.id = form.id;
        }

        setLoading(true);
        call(payload)
            .then(({ data }) => {
                const raw = typeof data === 'string' ? data : JSON.stringify(data || '');
                if (raw && raw.includes('<error>')) {
                    errorToast({ text: getLiteral('error_generalerror') });
                    setLoading(false);
                    return;
                }
                onSaveSuccess && onSaveSuccess();
                onCancel();
            })
            .catch((e) => {
                console.error(e);
                errorToast({ text: getLiteral('error_generalerror') });
                setLoading(false);
            });
    }, [canSave, form, isEdit, lockFactorAndMinStep, onCancel, onSaveSuccess, validate]);

    const beforeRenderUomOptions = useCallback((finalOptions) => {
        return finalOptions.map((opt) => ({
            ...opt,
            label: formatUnitOfMeasureOptionLabel(opt),
        }));
    }, []);

    const unitOfMeasureSelectActions = useMemo(() => {
        const canOpenValueListForNewUom =
            canManageUomStandardValueList && (!isSageProductMode || productCrud?.create);
        if (!canOpenValueListForNewUom) return undefined;
        return [
            {
                label: getLiteral('action_add_unit_of_measure'),
                iconType: 'plus',
                onClick: () => {
                    logEvent({
                        event: PRODUCTS.trueName,
                        submodule: 'valueList',
                        functionality: 'openUnitsOfMeasureFromProductRelationModal',
                    });
                    const url = getRoute(
                        '/settings/values-list',
                        `?entity=${PRODUCTS.trueName}&list=tblUnitsOfMeasure`,
                    );
                    if (url) window.open(url, '_blank', 'noopener,noreferrer');
                },
            },
        ];
    }, [canManageUomStandardValueList, isSageProductMode, productCrud?.create]);

    return (
        <Modal
            overlayClassName="fm-product-unit-relations-modal__overlay"
            className="fm-product-unit-relations-modal"
            useCornerClose={false}
            isOpen={open}
            onCancel={onCancel}
            onRequestClose={onCancel}
            onConfirm={onConfirm}
            isConfirmDisabled={!canSave}
            isConfirmLoading={loading}
            confirmText={getLiteral('action_save')}
            cancelText={getLiteral('action_cancel')}
            title={isEdit ? getLiteral('action_edit_unit_relation') : getLiteral('action_add_unit')}
        >
            <div className="fm-product-unit-relations-modal__fields">
                <Text
                    label={getLiteral('label_unit_name')}
                    mandatory
                    onChange={onChangeField('name')}
                    value={form.name}
                    error={errors.name}
                />
                <Select
                    label={getLiteral('label_unit_of_measure')}
                    hint={getLiteral('label_selectone')}
                    description={
                        <Markdown
                            className="fm-product-unit-relations-modal__hint-markdown"
                            content={getLiteral('helptext_uom_unit_select')}
                            overrides={{ a: { target: '_blank', rel: 'noopener noreferrer' } }}
                        />
                    }
                    list="tblUnitsOfMeasure"
                    beforeRenderValueList={beforeRenderUomOptions}
                    actions={unitOfMeasureSelectActions}
                    mandatory
                    onChange={onChangeField('unitOfMeasure')}
                    value={form.unitOfMeasure}
                    error={errors.unitOfMeasure}
                    readOnly={isEdit}
                />
                <Bool
                    label={getLiteral('label_base_unit')}
                    onChange={onChangeIsBase}
                    value={form.isBase}
                    readOnly={isEdit && persistedIsBase}
                    description={
                        isEdit && persistedIsBase
                            ? getLiteral('label_unit_base_readonly_tooltip')
                            : undefined
                    }
                />
                {form.isBase && (
                    <>
                        <Advice
                            type="info"
                            showIcon
                            showCollapse={false}
                            overrides={{ Text: { as: 'div' } }}
                        >
                            <Markdown
                                className="fm-product-unit-relations-modal__hint-markdown"
                                content={getLiteral('warning_uom_base_unit_change')}
                                overrides={{ a: { target: '_blank', rel: 'noopener noreferrer' } }}
                            />
                        </Advice>
                        <Spacer y={1} />
                    </>
                )}
                <Decimal
                    label={getLiteral('label_factor')}
                    description={
                        <Markdown
                            className="fm-product-unit-relations-modal__hint-markdown"
                            content={getLiteral('helptext_uom_factor')}
                            overrides={{ a: { target: '_blank', rel: 'noopener noreferrer' } }}
                        />
                    }
                    mandatory
                    hint="0"
                    variableDecimalDigits
                    onChange={onChangeField('factor')}
                    value={form.factor}
                    error={errors.factor}
                    readOnly={lockFactorAndMinStep}
                />
                <Decimal
                    label={getLiteral('label_precision')}
                    description={
                        <Markdown
                            className="fm-product-unit-relations-modal__hint-markdown"
                            content={getLiteral('helptext_uom_precision')}
                            overrides={{ a: { target: '_blank', rel: 'noopener noreferrer' } }}
                        />
                    }
                    mandatory
                    hint="0"
                    variableDecimalDigits
                    onChange={onChangeField('minStep')}
                    value={form.minStep}
                    error={errors.minStep}
                    readOnly={lockFactorAndMinStep}
                />
                <Bool
                    label={getLiteral('label_use_special_price')}
                    onChange={onChangeField('useSpecialPrice')}
                    value={form.useSpecialPrice}
                />
                {form.useSpecialPrice && (
                    <Decimal
                        label={getLiteral('label_final_price')}
                        description={
                            <Markdown
                                className="fm-product-unit-relations-modal__hint-markdown"
                                content={getLiteral('helptext_uom_final_price')}
                                overrides={{ a: { target: '_blank', rel: 'noopener noreferrer' } }}
                            />
                        }
                        mandatory
                        limitDecimals={specialPriceDecimals}
                        onChange={onChangeField('specialPrice')}
                        value={form.specialPrice}
                        error={errors.specialPrice}
                    />
                )}
                <Bool
                    label={getLiteral('label_active')}
                    onChange={onChangeField('isActive')}
                    value={form.isActive}
                    readOnly={form.isBase}
                    description={
                        form.isBase
                            ? getLiteral('helptext_uom_base_relation_active_readonly')
                            : undefined
                    }
                />
            </div>
        </Modal>
    );
});

export default ProductUnitRelationsModal;
